import * as Popover from "@radix-ui/react-popover";
import { Command } from "cmdk";
import { Check, ChevronDown, Search } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import { useLocale } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";
import { formatList } from "@/shared/lib/format";
import { normalizeSearch } from "@/shared/lib/search";
import type { ComboboxOption } from "@/shared/ui/Combobox";
import {
  controlSizeClassName,
  inputClassName,
  listItemClassName,
  type ControlSize,
} from "@/shared/ui/styles";

interface MultiComboboxProps {
  options: readonly ComboboxOption[];
  /** The chosen values, in the order they were picked. */
  value: readonly string[];
  onChange: (value: string[]) => void;
  /** Shown in the trigger when nothing is chosen. */
  placeholder: string;
  searchPlaceholder: string;
  /** Shown when the search matches nothing. */
  emptyLabel: string;
  /** At most this many can be chosen; the rest of the list is disabled once it's reached. */
  max?: number;
  /** Shown under the list once `max` is reached. */
  fullLabel?: string;
  /** What the trigger shows instead of the chosen labels. */
  children?: ReactNode;
  disabled?: boolean;
  size?: ControlSize;
  triggerClassName?: string;
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

/**
 * Pick several options from a long list by typing, as Combobox does for one. Each pick toggles
 * the option and keeps the list open.
 */
export function MultiCombobox({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  max,
  fullLabel,
  children,
  disabled,
  size = "sm",
  triggerClassName,
  ...aria
}: MultiComboboxProps) {
  const { locale } = useLocale();
  const [search, setSearch] = useState("");
  const chosen = new Set(value);
  const full = max !== undefined && value.length >= max;

  const searchable = useMemo(
    () =>
      options.map((option) => ({
        option,
        text: normalizeSearch(
          [option.label, option.description ?? "", ...(option.keywords ?? [])].join(" "),
        ),
      })),
    [options],
  );
  const needle = normalizeSearch(search);
  const visible = needle
    ? searchable.filter((entry) => entry.text.includes(needle)).map((entry) => entry.option)
    : options;

  const toggle = (next: string) => {
    if (chosen.has(next)) onChange(value.filter((item) => item !== next));
    else if (!full) onChange([...value, next]);
  };

  const labels = value
    .map((item) => options.find((option) => option.value === item)?.label)
    .filter((label) => label !== undefined);

  return (
    <Popover.Root
      onOpenChange={(open) => {
        if (!open) setSearch("");
      }}
      modal
    >
      <Popover.Trigger
        role="combobox"
        aria-haspopup="listbox"
        disabled={disabled}
        className={cn(
          inputClassName,
          controlSizeClassName[size],
          "flex cursor-pointer items-center gap-2 pe-2 text-start data-[state=open]:border-brand data-[state=open]:ring-3 data-[state=open]:ring-brand-ring",
          triggerClassName,
        )}
        {...aria}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {children ??
            (labels.length > 0 ? (
              <span className="user-text min-w-0 truncate text-ink">
                {formatList(labels, locale)}
              </span>
            ) : (
              <span className="truncate text-ink-subtle">{placeholder}</span>
            ))}
        </span>
        <ChevronDown className="size-4 shrink-0 text-ink-faint" aria-hidden="true" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          className="z-70 w-(--radix-popover-trigger-width) min-w-60 animate-pop-in overflow-hidden rounded-xl border border-line bg-surface shadow-overlay"
        >
          <Command shouldFilter={false} loop label={searchPlaceholder}>
            <div className="flex items-center gap-2 border-b border-line px-2.5">
              <Search className="size-3.5 shrink-0 text-ink-faint" aria-hidden="true" />
              <Command.Input
                value={search}
                onValueChange={setSearch}
                placeholder={searchPlaceholder}
                className="h-9 min-w-0 flex-1 bg-transparent text-[14px] text-ink outline-none placeholder:text-ink-subtle"
              />
            </div>
            <Command.List className="max-h-64 overflow-y-auto p-1">
              {visible.length === 0 ? (
                <p className="px-2 py-6 text-center text-[13.5px] text-ink-muted">{emptyLabel}</p>
              ) : null}
              {visible.map((option) => (
                <Command.Item
                  key={option.value}
                  value={option.value}
                  disabled={full && !chosen.has(option.value)}
                  onSelect={() => {
                    toggle(option.value);
                  }}
                  className={listItemClassName}
                >
                  {option.leading}
                  <span className="min-w-0 flex-1">
                    <span className="user-text block truncate">{option.label}</span>
                    {option.description ? (
                      <span className="block truncate text-[12.5px] text-ink-subtle">
                        {option.description}
                      </span>
                    ) : null}
                  </span>
                  {chosen.has(option.value) ? (
                    <Check className="size-4 shrink-0 text-brand" aria-hidden="true" />
                  ) : null}
                </Command.Item>
              ))}
            </Command.List>
            {full && fullLabel ? (
              <p className="border-t border-line px-3 py-2 text-[12.5px] text-ink-subtle">
                {fullLabel}
              </p>
            ) : null}
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
